import React, { useEffect, useState, useRef, useMemo } from 'react';
import { ArrowLeftOutlined, LoadingOutlined } from '@ant-design/icons';
import CustomCalendar from '../../components/CustomCalendar';
import './DoctorSchedulePage.scss';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Row, Col, List, Button, Avatar, message, Select, Alert } from 'antd';
import { getDoctorApi } from '../../api/user';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import NoAvatar from '../../assets/img/no-avatar-iso.jpg';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getTurnPeriodPortalApi, getHolidaysApi } from '../../api/portal';
import { getServicesApi } from '../../api/service';
import PortalTurnDrawer from '../../components/PortalTurnDrawer/PortalTurnDrawer';
import { onPrimaryDataRead } from '../../actions';
import { generateSlots } from '../../utils/functionTurns';
import {
  getServiceIdsAvailableAtBranch,
  isServiceAvailableAtBranch,
  mergeExtraDayHours,
  buildCalendarWeekSchedule,
  getFilteredScheduleHours,
  getDoctorPerBlockServiceIds,
} from '../../utils/scheduleAvailability';
import DescriptionAlert from '../../components/DescriptionAlert/DescriptionAlert';
import Footer from '../../components/Footer';
dayjs.extend(utc);
dayjs.extend(timezone);

export default function DoctorSchedulePage(props) {
  const { id, type } = props.match.params;
  const selectedBranchIdFromStore = useSelector(
    (store) => store.portalFilters?.selectedBranchId,
  );

  const [selectedDay, setSelectedDay] = useState(dayjs()); // Día seleccionado
  const [doctorData, setDoctorData] = useState([]);
  const [configSchedule, setConfigSchedule] = useState();
  const [slots, setSlots] = useState([]);
  const [turn, setTurn] = useState([]);
  const [allTurns, setAllTurns] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const branchPreferenceApplied = useRef(false);
  const [draftTurnFormValues, setDraftTurnFormValues] = useState(null);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [doctorUsesDefaultBranch, setDoctorUsesDefaultBranch] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [servicesList, setServicesList] = useState([]);
  const [showServiceClearedHint, setShowServiceClearedHint] = useState(false);
  const [effectiveConfigForDay, setEffectiveConfigForDay] = useState(null);

  const [reload, setReload] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSlot, setDrawerSlot] = useState(null);
  const [drawerBranch, setDrawerBranch] = useState(null);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const { tenant, tenantTimezone, tenantId, studies, countryCode, branches } =
    useSelector((store) => store.tenantData);

  // Referencias a los componentes
  const slotsRef = useRef(null);

  // Cargar feriados desde la API
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const currentYear = dayjs().year();

        const response = await getHolidaysApi(
          tenantId,
          currentYear,
          countryCode,
        );

        if (response.ok) {
          setHolidays(response.holidays);
        } else {
          console.error('Error al obtener feriados:', response.message);
          message.warning('No se pudieron cargar los feriados');
          setHolidays([]);
        }
      } catch (error) {
        console.error('Error al obtener feriados:', error);
        setHolidays([]);
      }
    };

    fetchHolidays();
  }, [tenantId, countryCode]);

  const handleScroll = (ref) => {
    ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const liveScheduleHours = doctorData?.scheduleConfig?.hours || [];
  const isPerBlockService =
    doctorData?.scheduleConfig?.serviceConfigMode === 'perBlock';

  const calendarWeekSchedule = useMemo(() => {
    const filtered = getFilteredScheduleHours({
      hours: liveScheduleHours,
      branchId: selectedBranch,
      serviceId: selectedService,
      isPerBlock: isPerBlockService,
    });
    return buildCalendarWeekSchedule(filtered);
  }, [liveScheduleHours, selectedBranch, selectedService, isPerBlockService]);

  const serviceIdsAvailableAtBranch =
    selectedBranch === 'all' || !selectedBranch
      ? new Set(servicesList.map((s) => String(s._id || s.id)))
      : getServiceIdsAvailableAtBranch(
          liveScheduleHours,
          allTurns,
          selectedBranch,
        ) || new Set();

  // Si cambió la sede: limpiar servicio solo si no hay horario semanal ni extraDay en esa sede
  useEffect(() => {
    if (
      !selectedService ||
      doctorData?.scheduleConfig?.serviceConfigMode !== 'perBlock' ||
      !selectedBranch ||
      selectedBranch === 'all' ||
      initialLoading
    ) {
      return;
    }
    const available = isServiceAvailableAtBranch(
      selectedService,
      selectedBranch,
      liveScheduleHours,
      allTurns,
    );
    if (!available) {
      setSelectedService(null);
      setShowServiceClearedHint(true);
      message.warning(
        'El servicio elegido no está disponible en esta sede. Elegí otro tipo de atención.',
        5,
      );
    } else {
      setShowServiceClearedHint(false);
    }
  }, [
    selectedBranch,
    selectedService,
    liveScheduleHours,
    allTurns,
    doctorData?.scheduleConfig?.serviceConfigMode,
    initialLoading,
  ]);

  // Quitar el hint cuando el usuario elige un servicio o vuelve a "Todas las sedes"
  useEffect(() => {
    if (selectedService || selectedBranch === 'all' || !selectedBranch) {
      setShowServiceClearedHint(false);
    }
  }, [selectedService, selectedBranch]);

  // Extraer las sedes únicas del médico cuando se carga
  useEffect(() => {
    // Si doctorData todavía no cargó (estado inicial vacío), no tocar nada
    if (!doctorData?.scheduleConfig) return;

    if (doctorData.scheduleConfig.hours && branches?.length >= 2) {
      // Obtener los branchIds únicos de los horarios del médico
      const uniqueBranchIds = [
        ...new Set(
          doctorData.scheduleConfig.hours
            .filter((hour) => hour.branchId)
            .map((hour) => hour.branchId?.toString?.() || hour.branchId),
        ),
      ];

      // Mapear los IDs a las sedes completas
      let doctorBranches = uniqueBranchIds
        .map((branchId) => {
          const bid = branchId?.toString?.() || branchId;
          const branch = branches.find(
            (b) => (b._id?.toString?.() || b._id) === bid,
          );
          return branch || null;
        })
        .filter(Boolean);

      // Si el médico no tiene sede configurada en sus horarios, usa la sede principal por defecto
      if (doctorBranches.length === 0) {
        const mainBranch = branches.find((b) => b.isMain) || branches[0];
        if (mainBranch) {
          doctorBranches = [mainBranch];
          setDoctorUsesDefaultBranch(true);
        } else {
          setDoctorUsesDefaultBranch(false);
        }
      } else {
        setDoctorUsesDefaultBranch(false);
      }

      setAvailableBranches(doctorBranches);

      // Aplicar preferencia de sede guardada en el reducer solo la primera vez que carga el médico.
      // Después de eso el usuario puede cambiar libremente el select.
      if (!branchPreferenceApplied.current) {
        branchPreferenceApplied.current = true;
        const preferredId = selectedBranchIdFromStore;
        if (preferredId && doctorBranches.length > 0) {
          const found = doctorBranches.find(
            (b) => (b._id?.toString?.() || b._id) === String(preferredId),
          );
          setSelectedBranch(found ? String(preferredId) : 'all');
        } else if (doctorBranches.length > 0) {
          setSelectedBranch('all');
        } else {
          setSelectedBranch(null);
        }
      }
    } else {
      setAvailableBranches([]);
      setDoctorUsesDefaultBranch(false);
    }
  }, [doctorData, branches]);

  // Cargar servicios cuando el médico tiene servicios por bloque (para el selector)
  useEffect(() => {
    if (doctorData?.scheduleConfig?.serviceConfigMode !== 'perBlock') {
      setServicesList([]);
      return;
    }
    const loadServices = async () => {
      try {
        const res = await getServicesApi(tenantId);
        const ids = getDoctorPerBlockServiceIds(
          doctorData.scheduleConfig,
          allTurns,
        );
        if (!ids.length) {
          setServicesList([]);
          return;
        }
        const list = (res?.services || []).filter((s) =>
          ids.some((id) => String(s._id || s.id) === String(id)),
        );
        setServicesList(list);
      } catch (e) {
        setServicesList([]);
      }
    };
    loadServices();
  }, [doctorData?.scheduleConfig, allTurns, tenantId]);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);

      let userResponse;

      if (type === 'studie') {
        userResponse = {
          user: studies.find((studie) => studie.id === id),
        };
      } else {
        userResponse = await getDoctorApi({ tenantId, id });
      }
      setDoctorData(userResponse?.user);

      if (id) {
        let dataSizeSlot = userResponse?.user?.defaultSizeSlot;

        const periodTurnsResponse = await getTurnPeriodPortalApi(
          id,
          tenantId,
          type,
        );

        setAllTurns(periodTurnsResponse?.turns);

        let filterDataDay = await periodTurnsResponse?.turns?.find(
          (turn) =>
            dayjs(selectedDay).format('DD') == turn.day &&
            dayjs(selectedDay).format('MM') == turn.month &&
            dayjs(selectedDay).format('YYYY') == turn.year,
        );

        setTurn(filterDataDay);

        // Live completo a generateSlots: el merge/enriquecimiento y el filtro por sede
        // ocurren dentro de generateSlots (después de resolver snapshot + live).
        let filteredDayTurn = filterDataDay;
        if (
          selectedBranch &&
          selectedBranch !== 'all' &&
          filteredDayTurn?.extraDay &&
          filteredDayTurn?.scheduleConfig?.hours?.length > 0 &&
          userResponse?.user?.scheduleConfig?.hours
        ) {
          const sb = String(selectedBranch);
          const mergedExtraHours = mergeExtraDayHours(
            userResponse.user.scheduleConfig.hours,
            filteredDayTurn.scheduleConfig.hours,
          ).filter((h) => (h.branchId?.toString?.() || h.branchId) === sb);
          filteredDayTurn = {
            ...filteredDayTurn,
            scheduleConfig: {
              ...filteredDayTurn.scheduleConfig,
              hours: mergedExtraHours,
            },
          };
        }

        // perBlock: el live va completo a generateSlots.
        // Filtros por sede y servicio se aplican DESPUÉS del merge snapshot+live.
        const sc = userResponse?.user?.scheduleConfig;
        const isPerBlock =
          sc?.serviceConfigMode === 'perBlock' && sc?.hours?.length;
        if (isPerBlock) {
          if (
            selectedService &&
            filteredDayTurn?.extraDay &&
            filteredDayTurn?.scheduleConfig?.hours?.length > 0
          ) {
            const mergedForService = mergeExtraDayHours(
              userResponse?.user?.scheduleConfig?.hours,
              filteredDayTurn.scheduleConfig.hours,
            ).filter((h) =>
              (h.serviceIds || []).some(
                (sid) => String(sid) === String(selectedService),
              ),
            );
            filteredDayTurn = {
              ...filteredDayTurn,
              scheduleConfig: {
                ...filteredDayTurn.scheduleConfig,
                hours: mergedForService,
              },
            };
          }
        }

        // Generar los horarios basados en el turno y la sede (y servicio si perBlock)
        const response = await generateSlots({
          sizeSlot:
            filteredDayTurn?.shifts?.length > 0 || filteredDayTurn?.extraDay
              ? filteredDayTurn?.sizeSlot
              : dataSizeSlot,
          turns: filteredDayTurn,
          userTurn: userResponse?.user,
          date: selectedDay,
          tenantTimezone,
          holidays,
          branches,
          selectedServiceId: selectedService || null,
          selectedBranchId: selectedBranch || null,
        });

        setSlots(response?.filteredSlots);
        setConfigSchedule(response?.usualConfigSchedule);
        setEffectiveConfigForDay(response?.effectiveConfig || null);
      }
      setLoading(false);
      setInitialLoading(false);
    };

    getData();
    setReload(false);
  }, [id, selectedDay, reload, holidays, selectedBranch, selectedService]);

  const handleDaySelect = (day) => {
    setSelectedDay(day); // Actualiza el día seleccionado
    handleScroll(slotsRef);
  };

  // Función helper para encontrar la sede de un slot específico.
  // - scheduleConfig=null → usa la config live completa del médico (días normales)
  // - scheduleConfig=snapshot → usa el snapshot del extraDay para que el branchId sea el correcto
  const findBranchForSlot = (slot, scheduleConfig = null) => {
    const hoursSource =
      scheduleConfig?.hours || doctorData?.scheduleConfig?.hours;
    if (!slot?.hourStart || !hoursSource?.length || !branches?.length) {
      return null;
    }

    try {
      const slotTime = dayjs(slot.hourStart).tz(tenantTimezone);
      const dayOfWeek = slotTime.locale('es').format('dd').toLowerCase();
      const slotMinutes = slotTime.hour() * 60 + slotTime.minute();

      const matchingHours = hoursSource.filter((hour) => {
        try {
          // Snapshots viejos sin `days` se validan solo por rango horario (compatibilidad).
          if (hour.days?.length > 0 && !hour.days.includes(dayOfWeek)) {
            return false;
          }

          const hourStart = dayjs(hour.start).tz(tenantTimezone);
          const hourEnd = dayjs(hour.end).tz(tenantTimezone);
          const startMinutes = hourStart.hour() * 60 + hourStart.minute();
          let endMinutes = hourEnd.hour() * 60 + hourEnd.minute();
          if (endMinutes === 0) endMinutes = 1440;

          return slotMinutes >= startMinutes && slotMinutes < endMinutes;
        } catch (e) {
          return false;
        }
      });

      const matchingHour =
        matchingHours.find((h) => h.branchId) || matchingHours[0];

      if (matchingHour?.branchId) {
        const branch = branches.find(
          (b) => String(b._id) === String(matchingHour.branchId),
        );
        if (branch) return branch;
      }

      if (matchingHour) {
        return branches.find((b) => b.isMain) || branches[0] || null;
      }
    } catch (error) {
      // ignore
    }

    return null;
  };

  const addTurn = (slot) => {
    let selectedBranchData = null;

    if (selectedBranch === 'all') {
      // Para extraDay: usar el snapshot (tiene los branchIds correctos del día).
      // Para días normales: null → cae al live config completo del médico.
      selectedBranchData = findBranchForSlot(
        slot,
        effectiveConfigForDay || null,
      );
      if (!selectedBranchData && branches?.length > 0) {
        selectedBranchData = branches.find((b) => b.isMain) || branches[0];
      }
    } else if (selectedBranch) {
      selectedBranchData = availableBranches.find(
        (branch) =>
          (branch._id?.toString?.() || branch._id) === String(selectedBranch),
      );
    }

    setDrawerSlot(slot);
    setDrawerBranch(selectedBranchData);
    setDrawerOpen(true);
  };

  return (
    <div className="doctor-schedule-page">
      {/* Header */}
      <header
        className="header"
        style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}
      >
        <Link to="/">
          <ArrowLeftOutlined className="back-arrow" />
        </Link>
        <Avatar
          alt="Foto perfil"
          src={
            doctorData?.profileImage
              ? `https://storage.googleapis.com/bs-proyecto1.appspot.com/${tenant}/profiles/${doctorData?.profileImage}`
              : NoAvatar
          }
          style={{
            width: '50px',
            height: '50px',
            objectFit: 'cover',
            borderRadius: '50%',
            marginRight: '16px', // Espaciado entre la foto y el texto
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h1 className="doctor-title" style={{ marginBottom: 2 }}>
            {doctorData?.name} {doctorData?.lastname}
          </h1>
          {doctorData?.medicalSpeciality && (
            <div
              className="doctor-speciality"
              style={{
                color: '#888',
                fontWeight: 400,
                fontSize: '1rem',
                marginTop: 0,
                marginBottom: 0,
                lineHeight: 1.2,
              }}
            >
              {doctorData.medicalSpeciality}
            </div>
          )}
        </div>
      </header>

      {doctorData?.scheduleConfig?.description && (
        <DescriptionAlert
          description={doctorData?.scheduleConfig?.description}
        />
      )}

      {doctorData?.scheduleConfig?.quantityDaysPortal !== 0 && (
        <>
          {/* Sede única: siempre visible aunque no haya bloque de filtros */}
          {availableBranches.length === 1 && liveScheduleHours.length !== 0 && (
            <div className="schedule-branch-info">
              <span className="schedule-branch-info__label">
                📍 Sede de atención:
              </span>{' '}
              <strong>{availableBranches[0]?.name}</strong>
              {availableBranches[0]?.address
                ? ` — ${availableBranches[0].address}`
                : ''}
            </div>
          )}
          {/* Mostrar bloque de filtros solo si hay servicios por bloque O múltiples sedes */}
          {(doctorData?.scheduleConfig?.serviceConfigMode === 'perBlock' &&
            servicesList?.length > 0) ||
          availableBranches.length > 1 ? (
            <div className="schedule-filters">
              {/* Guía para el paciente: paso 1 obligatorio, paso 2 opcional */}
              {(() => {
                const isPerBlock =
                  doctorData?.scheduleConfig?.serviceConfigMode ===
                    'perBlock' && servicesList?.length > 0;
                const msg = isPerBlock
                  ? availableBranches.length >= 2
                    ? 'Primero elegí el tipo de atención que necesitás; así verás los horarios disponibles. La sede es opcional.'
                    : 'Primero elegí el tipo de atención que necesitás; así verás los horarios disponibles.'
                  : availableBranches.length > 1
                    ? 'Elegí la sede donde querés atenderte (opcional: podés ver todas).'
                    : null;
                return msg ? (
                  <p className="schedule-filters__intro">{msg}</p>
                ) : null;
              })()}
              <div
                className={`schedule-filters__card ${
                  showServiceClearedHint
                    ? 'schedule-filters__card--cleared-hint'
                    : ''
                }`}
              >
                {showServiceClearedHint && (
                  <p className="schedule-filters__cleared-message">
                    El servicio elegido no está disponible en esta sede. Elegí
                    otro tipo de atención.
                  </p>
                )}
                {doctorData?.scheduleConfig?.serviceConfigMode === 'perBlock' &&
                  servicesList?.length > 0 && (
                    <div className="schedule-filters__field">
                      <p className="schedule-filters__steps">
                        Paso 1 — Obligatorio
                      </p>
                      <label className="schedule-filters__label">
                        Tipo de atención
                      </label>
                      <Select
                        value={selectedService || undefined}
                        onChange={(value) => setSelectedService(value)}
                        placeholder="Seleccione el tipo de atención"
                        className={`schedule-filters__select ${
                          showServiceClearedHint
                            ? 'schedule-filters__select--error'
                            : ''
                        }`}
                        size="large"
                        allowClear
                      >
                        {servicesList.map((s) => {
                          const sid = String(s._id || s.id);
                          const available =
                            serviceIdsAvailableAtBranch.has(sid);
                          if (!s.public) return null;
                          return (
                            <Select.Option
                              key={sid}
                              value={sid}
                              disabled={!available}
                            >
                              {s.name}
                              {!available &&
                              selectedBranch &&
                              selectedBranch !== 'all'
                                ? ' (no disponible en esta sede)'
                                : ''}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </div>
                  )}
                {availableBranches.length > 1 && (
                  <div className="schedule-filters__field">
                    {doctorData?.scheduleConfig?.serviceConfigMode ===
                      'perBlock' &&
                      servicesList?.length > 0 && (
                        <p className="schedule-filters__steps">
                          Paso 2 — Opcional
                        </p>
                      )}
                    <label className="schedule-filters__label">Sede</label>
                    <Select
                      value={selectedBranch}
                      onChange={(value) =>
                        setSelectedBranch(value ? String(value) : null)
                      }
                      className="schedule-filters__select"
                      size="large"
                    >
                      <Select.Option key="all" value="all">
                        Todas las sedes
                      </Select.Option>
                      {availableBranches.map((branch) => (
                        <Select.Option
                          key={branch._id?.toString?.() || branch._id}
                          value={branch._id?.toString?.() || branch._id}
                        >
                          {branch.name}
                          {branch.address ? ` — ${branch.address}` : ''}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                )}
              </div>

              {/* Descripción y precio del servicio seleccionado (como en el form) */}
              {selectedService &&
                doctorData?.scheduleConfig?.serviceConfigMode ===
                  'perBlock' && (
                  <div className="schedule-filters__service-detail">
                    {(() => {
                      const svc = servicesList.find(
                        (s) => (s._id || s.id) === selectedService,
                      );
                      if (!svc) return null;
                      return (
                        <>
                          <h4 className="schedule-filters__service-name">
                            {svc.name}
                          </h4>
                          {svc.description && (
                            <p className="schedule-filters__service-desc">
                              {svc.description}
                            </p>
                          )}
                          {svc.price != null && svc.price !== '' && (
                            <p className="schedule-filters__service-price">
                              Valor:{' '}
                              {Number(svc.price).toLocaleString('es-AR', {
                                style: 'currency',
                                currency: 'ARS',
                                minimumFractionDigits: 0,
                              })}
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
            </div>
          ) : null}
          {/* Contenido Principal */}
          {initialLoading ? (
            <div className="calendar-loading">
              <LoadingOutlined style={{ fontSize: 32 }} />
            </div>
          ) : doctorData?.scheduleConfig?.serviceConfigMode === 'perBlock' &&
            servicesList?.length > 0 &&
            !selectedService ? (
            // ── Pantalla centrada: elegir tipo de atención primero ──────────────
            <div className="service-required-prompt">
              <div className="service-required-prompt__icon">🗓️</div>
              <p className="service-required-prompt__title">
                Elegí el tipo de atención
              </p>
              <p className="service-required-prompt__subtitle">
                Seleccioná el servicio en el filtro de arriba para ver los días
                y horarios disponibles.
              </p>
            </div>
          ) : (
            <Row className="content" gutter={[24, 24]}>
              {/* Calendario */}
              <Col xs={24} md={8} className="calendar-section">
                <CustomCalendar
                  onDaySelect={handleDaySelect}
                  tenantTimezone={tenantTimezone}
                  scheduleConfig={
                    selectedBranch &&
                    selectedBranch !== 'all' &&
                    doctorData?.scheduleConfig?.hours
                      ? {
                          ...doctorData.scheduleConfig,
                          hours: doctorData.scheduleConfig.hours.filter(
                            (hour) =>
                              (hour.branchId?.toString?.() || hour.branchId) ===
                              String(selectedBranch),
                          ),
                        }
                      : doctorData?.scheduleConfig || []
                  }
                  liveScheduleHours={liveScheduleHours}
                  calendarWeekSchedule={calendarWeekSchedule}
                  configSchedule={configSchedule}
                  allTurns={allTurns}
                  holidays={holidays}
                  selectedBranchId={
                    selectedBranch && selectedBranch !== 'all'
                      ? selectedBranch
                      : null
                  }
                  selectedServiceId={
                    doctorData?.scheduleConfig?.serviceConfigMode === 'perBlock'
                      ? selectedService
                      : null
                  }
                />
              </Col>

              {/* Detalle */}
              <Col xs={24} md={16}>
                {selectedDay ? (
                  <div className="details-section">
                    <p className="selected-date">
                      <strong>
                        {dayjs(selectedDay).format('dddd')}{' '}
                        {dayjs(selectedDay).format('D')} de{' '}
                        {dayjs(selectedDay).format('MMMM')}
                      </strong>
                    </p>
                    <p
                      className={
                        doctorData?.scheduleConfig?.serviceConfigMode ===
                          'perBlock' &&
                        servicesList?.length > 0 &&
                        !selectedService
                          ? 'details-section__hint details-section__hint--step1'
                          : ''
                      }
                      style={{
                        textAlign: 'center',
                        fontSize: '12pt',
                        color: 'grey',
                      }}
                    >
                      {doctorData?.scheduleConfig?.serviceConfigMode ===
                        'perBlock' &&
                      servicesList?.length > 0 &&
                      !selectedService
                        ? 'Elegí el tipo de atención arriba (Paso 1) para ver los horarios disponibles. Después elegí día y horario.'
                        : slots?.length > 0
                          ? 'Seleccione un horario'
                          : 'Agenda completa'}
                    </p>
                    {selectedBranch === 'all' &&
                    availableBranches.length > 1 ? (
                      // ── Vista agrupada por sede ──────────────────────────────────
                      loading ? (
                        <div style={{ textAlign: 'center', padding: '24px 0' }}>
                          <LoadingOutlined style={{ fontSize: 24 }} />
                        </div>
                      ) : slots?.length === 0 ? (
                        <p
                          style={{
                            textAlign: 'center',
                            color: 'grey',
                            fontSize: '12pt',
                          }}
                        >
                          No hay más turnos, intente otro día.
                        </p>
                      ) : (
                        (() => {
                          // Agrupar slots por sede manteniendo el orden de aparición.
                          // ExtraDay: usa el snapshot (branchIds correctos para ese día).
                          // Día normal: null → live config completo del médico.
                          const configForBranchResolution =
                            effectiveConfigForDay || null;
                          const groups = [];
                          const seen = new Map();
                          (slots || []).forEach((slot) => {
                            const branch = findBranchForSlot(
                              slot,
                              configForBranchResolution,
                            );
                            const key = branch?._id || 'sin-sede';
                            if (!seen.has(key)) {
                              seen.set(key, { branch, items: [] });
                              groups.push(seen.get(key));
                            }
                            seen.get(key).items.push(slot);
                          });

                          return groups.map(({ branch, items }) => (
                            <div
                              key={branch?._id || 'sin-sede'}
                              className="slots-group"
                              ref={slotsRef}
                            >
                              <p className="slots-group__header">
                                📍 {branch?.name || 'Sin sede'}
                              </p>
                              <div className="slots-group__grid">
                                {items.map((slot, i) => (
                                  <Button
                                    key={i}
                                    className="date"
                                    onClick={() => addTurn(slot)}
                                  >
                                    {dayjs(slot?.hourStart)
                                      .tz(tenantTimezone)
                                      .format('HH:mm')}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          ));
                        })()
                      )
                    ) : (
                      // ── Vista plana (sede única o filtro activo) ─────────────────
                      <>
                        {selectedBranch &&
                          selectedBranch !== 'all' &&
                          availableBranches.length > 1 && (
                            <p className="slots-group__header">
                              📍{' '}
                              {
                                availableBranches.find(
                                  (b) =>
                                    (b._id?.toString?.() || b._id) ===
                                    String(selectedBranch),
                                )?.name
                              }
                            </p>
                          )}
                        <List
                          grid={{ gutter: 24, xs: 3 }}
                          dataSource={slots}
                          size="small"
                          renderItem={(slot) => (
                            <List.Item ref={slotsRef}>
                              <Button
                                className="date"
                                onClick={() => addTurn(slot)}
                              >
                                <span>
                                  {dayjs(slot?.hourStart)
                                    .tz(tenantTimezone)
                                    .format('HH:mm')}
                                </span>
                              </Button>
                            </List.Item>
                          )}
                          locale={{
                            emptyText:
                              doctorData?.scheduleConfig?.serviceConfigMode ===
                                'perBlock' &&
                              servicesList?.length > 0 &&
                              !selectedService
                                ? 'Primero elegí el tipo de atención (Paso 1) arriba.'
                                : 'No hay más turnos, intente otro día.',
                          }}
                          loading={
                            loading && {
                              style: { width: '100%' },
                              size: 'large',
                              indicator: <LoadingOutlined />,
                            }
                          }
                        />
                      </>
                    )}
                  </div>
                ) : (
                  <p>Seleccione un día en el calendario.</p>
                )}
              </Col>
            </Row>
          )}{' '}
          {/* fin ternario loading / service-required / calendar */}
          <PortalTurnDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            slot={drawerSlot}
            selectedDay={selectedDay}
            doctor={doctorData}
            scheduleConfig={doctorData?.scheduleConfig}
            turnId={turn?._id}
            turnUser={id}
            turnType={type}
            turns={turn?.shifts}
            size={
              turn?.shifts?.length > 0
                ? turn?.sizeSlot
                : doctorData?.defaultSizeSlot
            }
            selectedBranch={drawerBranch}
            preselectedServiceId={
              doctorData?.scheduleConfig?.serviceConfigMode === 'perBlock'
                ? selectedService
                : null
            }
            setReload={setReload}
            initialValues={draftTurnFormValues}
            onDraftChange={setDraftTurnFormValues}
            initialStep={
              draftTurnFormValues?.dni &&
              draftTurnFormValues?.name &&
              draftTurnFormValues?.email &&
              draftTurnFormValues?.number
                ? 1
                : 0
            }
          />
        </>
      )}
      <Footer />
    </div>
  );
}
